export const GET_MARKETS_QUERY = /* GraphQL */ `
    query GetMarkets(
        $first: Int!
        $after: String
        $query: String
        $type: MarketType
        $sortKey: MarketsSortKeys
        $reverse: Boolean
    ) {
        markets(first: $first, after: $after, query: $query, type: $type, sortKey: $sortKey, reverse: $reverse) {
            nodes {
                id
                name
                conditions {
                    regionsCondition {
                        regions(first: 50) {
                            nodes {
                                id
                                name
                                ... on MarketRegionCountry {
                                    code
                                }
                            }
                        }
                    }
                }
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
    }
`;

export const MARKET_COMPANY_LOCATION_COUNTRY_CODE_FILTER = 'wildcard_company_location_with_country_code';

/** Uppercases, trims and de-duplicates a list of ISO country codes, dropping empty entries. */
export function normalizeCountryCodes(countryCodes: (string | undefined | null)[]): string[] {
    return [...new Set(countryCodes.map((code) => code?.trim().toUpperCase()).filter(Boolean))];
}

/**
 * Builds the `query` filter for the `markets` connection.
 *
 * `wildcard_company_location_with_country_code` only accepts a single value, so multiple
 * country codes are OR-ed together into one search-syntax group:
 *
 *   (wildcard_company_location_with_country_code:HU OR wildcard_company_location_with_country_code:RO)
 *
 * Returns `undefined` for an empty list so the caller can omit the argument entirely.
 */
export function buildCompanyLocationCountryCodesFilter(
    countryCodes: (string | undefined | null)[]
): string | undefined {
    const codes = normalizeCountryCodes(countryCodes);

    if (!codes.length) {
        return undefined;
    }

    const terms = codes.map((code) => `${MARKET_COMPANY_LOCATION_COUNTRY_CODE_FILTER}:${code}`);

    return terms.length === 1 ? terms[0] : `(${terms.join(' OR ')})`;
}

/** Joins several already-built filter expressions with AND, skipping empty ones. */
export function combineMarketFilters(...filters: (string | undefined | null)[]): string | undefined {
    const parts = filters.filter(Boolean);

    return parts.length ? parts.join(' AND ') : undefined;
}

/**
 * Indexes markets by the ISO country codes of their region condition, so a market returned by the
 * OR-ed wildcard filter above can be attributed back to the country code it was requested for.
 */
export function groupMarketsByCountryCode(markets: ShopifyMarket[]): Record<string, ShopifyMarket[]> {
    return markets.reduce<Record<string, ShopifyMarket[]>>((acc, market) => {
        market.conditions?.regionsCondition?.regions?.nodes?.forEach((region) => {
            if (!region.code) {
                return;
            }
            const code = region.code.toUpperCase();
            acc[code] = [...(acc[code] ?? []), market];
        });

        return acc;
    }, {});
}

export interface GetMarketsVariables {
    [key: string]: unknown;
    first: number;
    after?: string;
    query?: string;
    type?: MarketType;
    sortKey?: MarketsSortKeys;
    reverse?: boolean;
}

export type MarketType = 'CHANNEL' | 'COMPANY_LOCATION' | 'LOCATION' | 'NONE' | 'REGION';
export type MarketsSortKeys =
    | 'CREATED_AT'
    | 'ID'
    | 'MARKET_CONDITION_TYPES'
    | 'MARKET_TYPE'
    | 'NAME'
    | 'STATUS'
    | 'UPDATED_AT';
/** `code` is only present on `MarketRegionCountry` members of the `MarketRegion` interface. */
export interface ShopifyMarketRegion {
    id: string;
    name: string;
    code?: string;
}

export interface ShopifyMarketConditions {
    regionsCondition: {
        regions: { nodes: ShopifyMarketRegion[] };
    } | null;
}

export interface ShopifyMarket {
    id: string;
    name: string;
    conditions: ShopifyMarketConditions | null;
}

export interface GetMarketsResponse {
    markets: {
        nodes: ShopifyMarket[];
        pageInfo?: {
            hasNextPage: boolean;
            endCursor?: string;
        };
    };
}

/**
 * Just the ids of the shop's markets. Kept separate from `GET_MARKETS_QUERY`, whose nested
 * `regions(first: 50)` would be wasted when only the ids are needed.
 */
export const GET_MARKET_IDS_QUERY = /* GraphQL */ `
    query GetMarketIds($first: Int!, $after: String) {
        markets(first: $first, after: $after) {
            nodes {
                id
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
    }
`;

export interface GetMarketIdsResponse {
    markets: {
        nodes: { id: string }[];
        pageInfo?: {
            hasNextPage: boolean;
            endCursor?: string;
        };
    };
}
